/**
 * Checklist block (spec §7.4.11, §8.17): nested to-do items with checkmarks. Each item's text is a
 * single-line CodeMirror bound to its LoroText (imperative mount, §8.6), so concurrent typing merges.
 * Enter splits, Backspace on an empty item deletes it, Tab/Shift+Tab indent/outdent, Alt+↑/↓ move,
 * ↑/↓ move the focus; the drag handle reorders. "Hide completed" is a per-reader preference (I1).
 */
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { EditorState, Prec } from "@codemirror/state";
import { EditorView, keymap, placeholder } from "@codemirror/view";
import type { LoroMap, TreeID } from "loro-crdt";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { fromLoro, loroTextBinding } from "../../editors/loroText";
import { useLoroSelector } from "../../sync/hooks";
import type { DocHandle } from "../../sync/docManager";
import { getMeta, setMeta } from "../../sync/persistence";
import type { BlockComponentProps } from "../registry";
import { LoroTextInput, bodyMap } from "../textField";
import { useT } from "../../i18n";
import { addItem, deleteItem, indentItem, itemText, moveItem, outdentItem, placeNear, readItems, splitItem, stats, toggleItem, type ItemRow } from "./model";

type FocusTarget = { id: TreeID; at: "start" | "end" };

export function ChecklistBlock({ handle, treeId, node, readOnly, userId, onFocus }: BlockComponentProps) {
  const t = useT();
  const body = bodyMap(node);
  const rows = useLoroSelector(handle.doc, () => (body ? readItems(body) : []), (a, b) => JSON.stringify(a) === JSON.stringify(b)) ?? [];
  const views = useRef(new Map<TreeID, EditorView>());
  const pendingFocus = useRef<FocusTarget | null>(null);
  const [hideDone, setHideDone] = useState(false);
  const prefKey = `checklist-hide-completed:${handle.crdtDocId}:${treeId}`;
  useEffect(() => {
    void getMeta<boolean>(prefKey).then((v) => setHideDone(v === true));
  }, [prefKey]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  if (!body) return null;
  const visible = hideDone ? rows.filter((r) => !r.hiddenByCompleted) : rows;
  const { done, total } = stats(rows);

  const focus = (target: FocusTarget) => {
    const view = views.current.get(target.id);
    if (!view) {
      pendingFocus.current = target; // applied when the new row's editor mounts
      return;
    }
    view.focus();
    view.dispatch({ selection: { anchor: target.at === "start" ? 0 : view.state.doc.length } });
  };
  const neighbour = (id: TreeID, delta: -1 | 1): ItemRow | undefined => {
    const i = visible.findIndex((r) => r.id === id);
    return visible[i + delta];
  };
  const actions: ItemActions = {
    split: (id, at) => focus({ id: splitItem(handle.doc, body, id, at, userId), at: "start" }),
    removeEmpty: (row) => {
      if (row.hasChildren) return false;
      const prev = neighbour(row.id, -1);
      deleteItem(handle.doc, body, row.id);
      if (prev) focus({ id: prev.id, at: "end" });
      return true;
    },
    indent: (id) => indentItem(handle.doc, body, id),
    outdent: (id) => outdentItem(handle.doc, body, id),
    move: (id, delta) => moveItem(handle.doc, body, id, delta),
    focusNeighbour: (id, delta) => {
      const n = neighbour(id, delta);
      if (!n) return false;
      focus({ id: n.id, at: delta < 0 ? "end" : "start" });
      return true;
    },
  };

  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const from = visible.findIndex((r) => r.id === e.active.id);
    const to = visible.findIndex((r) => r.id === e.over!.id);
    placeNear(handle.doc, body, e.active.id as TreeID, e.over.id as TreeID, from < to ? "after" : "before");
  };

  return (
    <div className="rounded-md border border-neutral-200 p-2.5 dark:border-neutral-800" data-testid="checklist-block" onFocus={onFocus}>
      <div className="flex items-center gap-2">
        <LoroTextInput handle={handle} map={body} field="title" placeholder={t("checklist.title")} readOnly={readOnly} className="grow bg-transparent text-sm font-semibold outline-none placeholder:text-neutral-400" testId="checklist-title" />
        <span className="text-xs text-neutral-500" data-testid="checklist-progress">
          {t("checklist.progress", { done, total })}
        </span>
        <label className="flex items-center gap-1 text-xs text-neutral-500">
          <input
            type="checkbox"
            checked={hideDone}
            data-testid="checklist-hide-completed"
            onChange={(e) => {
              setHideDone(e.target.checked);
              void setMeta(prefKey, e.target.checked);
            }}
          />
          {t("checklist.hideDone")}
        </label>
      </div>
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <SortableContext items={visible.map((r) => r.id)} strategy={verticalListSortingStrategy}>
          <ul className="mt-1" data-testid="checklist-items">
            {visible.map((row) => (
              <ItemRowView
                key={row.id}
                handle={handle}
                body={body}
                row={row}
                readOnly={readOnly}
                userId={userId}
                actions={actions}
                register={(view) => {
                  if (view) {
                    views.current.set(row.id, view);
                    const target = pendingFocus.current;
                    if (target?.id === row.id) {
                      // focus at once — keys typed right after Enter belong to the new item — but keep the
                      // request until the frame ends: an editor remounted in the same commit (React
                      // StrictMode, or any re-mount) must receive it too
                      focus(target);
                      requestAnimationFrame(() => {
                        if (pendingFocus.current === target) pendingFocus.current = null;
                      });
                    }
                  } else views.current.delete(row.id);
                }}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      {!readOnly && (
        <button
          className="mt-1 flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
          data-testid="checklist-add"
          onClick={() => focus({ id: addItem(handle.doc, body, rows.filter((r) => r.parent === null).at(-1)?.id ?? null, userId), at: "start" })}
        >
          <Plus size={12} /> {t("checklist.add")}
        </button>
      )}
    </div>
  );
}

interface ItemActions {
  split(id: TreeID, at: number): void;
  removeEmpty(row: ItemRow): boolean;
  indent(id: TreeID): boolean;
  outdent(id: TreeID): boolean;
  move(id: TreeID, delta: -1 | 1): boolean;
  focusNeighbour(id: TreeID, delta: -1 | 1): boolean;
}

function ItemRowView({ handle, body, row, readOnly, userId, actions, register }: { handle: DocHandle; body: LoroMap; row: ItemRow; readOnly: boolean; userId: string; actions: ItemActions; register(view: EditorView | null): void }) {
  const t = useT();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id, disabled: readOnly });
  const mountRef = useRef<HTMLDivElement>(null);
  // the latest row and actions for the keymap, which is created once per editor
  const latest = useRef({ row, actions });
  useLayoutEffect(() => {
    latest.current = { row, actions };
  });

  useEffect(() => {
    const text = itemText(body, row.id);
    if (!mountRef.current || !text) return;
    const run = (fn: (view: EditorView) => boolean) => (view: EditorView) => fn(view);
    const view = new EditorView({
      parent: mountRef.current,
      state: EditorState.create({
        doc: text.toString(),
        extensions: [
          loroTextBinding(handle.doc, text),
          EditorView.lineWrapping,
          EditorState.readOnly.of(readOnly),
          EditorView.editable.of(!readOnly),
          placeholder(t("checklist.todo")),
          // one line per item: pasted line breaks become spaces
          // (never rewrite a change that came from Loro: the binding would write it back)
          EditorState.transactionFilter.of((tr) => {
            if (!tr.docChanged || tr.annotation(fromLoro) || !tr.newDoc.toString().includes("\n")) return tr;
            const changes: { from: number; to: number; insert: string }[] = [];
            tr.changes.iterChanges((fromA, toA, _fb, _tb, inserted) => {
              changes.push({ from: fromA, to: toA, insert: inserted.toString().replace(/\r?\n/g, " ") });
            });
            return { changes, selection: tr.selection };
          }),
          Prec.high(
            keymap.of([
              {
                key: "Enter",
                run: run((v) => {
                  const at = v.state.selection.main.head;
                  // leave this item before the new one exists, so no keystroke lands in the wrong item
                  v.contentDOM.blur();
                  latest.current.actions.split(latest.current.row.id, at);
                  return true;
                }),
              },
              {
                key: "Backspace",
                run: run((v) => {
                  const sel = v.state.selection.main;
                  if (!sel.empty || sel.head !== 0) return false;
                  if (v.state.doc.length === 0) return latest.current.actions.removeEmpty(latest.current.row);
                  return latest.current.row.depth > 0 ? latest.current.actions.outdent(latest.current.row.id) : false;
                }),
              },
              { key: "Tab", run: run(() => { latest.current.actions.indent(latest.current.row.id); return true; }) },
              { key: "Shift-Tab", run: run(() => { latest.current.actions.outdent(latest.current.row.id); return true; }) },
              { key: "Alt-ArrowUp", run: run(() => latest.current.actions.move(latest.current.row.id, -1)) },
              { key: "Alt-ArrowDown", run: run(() => latest.current.actions.move(latest.current.row.id, 1)) },
              { key: "ArrowUp", run: run(() => latest.current.actions.focusNeighbour(latest.current.row.id, -1)) },
              { key: "ArrowDown", run: run(() => latest.current.actions.focusNeighbour(latest.current.row.id, 1)) },
            ]),
          ),
        ],
      }),
    });
    register(view);
    return () => {
      register(null);
      view.destroy();
    };
    // one editor per item for the item's lifetime; row and actions are read through `latest`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id, readOnly]);

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.5 : 1, paddingLeft: row.depth * 20 }}
      className="group/item flex items-start gap-1 py-0.5"
      data-testid="checklist-item"
      data-item-id={row.id}
      data-depth={row.depth}
      data-checked={row.checked}
    >
      {!readOnly && (
        <button className="mt-0.5 cursor-grab text-neutral-300 opacity-0 group-hover/item:opacity-100" aria-label={t("checklist.drag")} {...attributes} {...listeners}>
          <GripVertical size={14} />
        </button>
      )}
      <input
        type="checkbox"
        className="mt-1"
        checked={row.checked}
        disabled={readOnly}
        aria-label={t("checklist.done")}
        data-testid="checklist-check"
        onChange={(e) => toggleItem(handle.doc, body, row.id, e.target.checked, userId)}
      />
      <div ref={mountRef} className={"min-w-0 grow text-sm [&_.cm-editor]:bg-transparent [&_.cm-focused]:outline-none " + (row.checked ? "text-neutral-400 line-through" : "")} data-testid="checklist-text" />
      {!readOnly && (
        <button className="mt-0.5 text-neutral-300 opacity-0 hover:text-red-600 group-hover/item:opacity-100" aria-label={t("checklist.delete")} data-testid="checklist-delete" onClick={() => deleteItem(handle.doc, body, row.id)}>
          <Trash2 size={13} />
        </button>
      )}
    </li>
  );
}
